/**
 * @fileoverview Component for loading and initializing shared spinner configurations.
 *
 * This component is responsible for loading spinner data from a shared link
 * (identified by spinnerId in the URL), creating a copy for the current user,
 * and then redirecting to the main application with the new spinner ID.
 * It handles loading states and error cases gracefully.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { spinnerService } from '../services/spinnerService';
import { toast } from 'react-hot-toast';
import { Frame } from '../screens/Frame';
import { auth } from '../firebase/config';

/**
 * Component that loads and creates a copy of a shared spinner configuration.
 * 
 * This component is rendered when a user visits a shared spinner link.
 * It validates the spinner ID, creates a copy for the current user,
 * and redirects to the main application with the new spinner ID.
 * 
 * @returns {JSX.Element} Either a loading screen or the main Frame component.
 */
export const SharedSpinner: React.FC = () => {
  const { spinnerId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [spinnerData, setSpinnerData] = useState<any>(null);

  // Effect to validate the shared spinner, create a copy, and redirect to main app
  useEffect(() => {
    /**
     * Validates the shared spinner ID, creates a copy for the current user,
     * and redirects to the main application with the new spinner ID.
     */
    const validateAndCreateCopy = async () => {
      if (!spinnerId) {
        toast.error(t('share.error'));
        navigate('/');
        return;
      }

      try {
        // Load the original shared spinner data from Firestore
        const originalSpinnerData = await spinnerService.getSpinner(spinnerId);
        if (!originalSpinnerData) {
          toast.error(t('share.notFound'));
          navigate('/');
          return;
        }
        
        // Set spinner data for meta tags
        setSpinnerData(originalSpinnerData);

        // Check if current user is the owner of this spinner
        const currentUserId = auth.currentUser?.uid || '';
        const isOwner = originalSpinnerData.userId === currentUserId;

        if (isOwner) {
          // If user is the owner, just redirect to the original spinner
          navigate('/', { state: { spinnerId: spinnerId } });
          toast.success('Opening your shared spinner!');
        } else {
          // If user is not the owner, create a copy
          const copyData = {
            participants: originalSpinnerData.participants,
            style: originalSpinnerData.style,
            language: originalSpinnerData.language,
            theme: originalSpinnerData.theme,
            sound: originalSpinnerData.sound,
            spinTime: originalSpinnerData.spinTime,
            name: originalSpinnerData.name,
            createdAt: new Date(),
            userId: auth.currentUser?.uid || '',
            lastAccessed: new Date(),
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year
            isActive: true
          };

          // Create the copy in Firestore (works for both authenticated and anonymous users)
          const newSpinnerId = await spinnerService.shareSpinner(copyData);
          
          // Redirect to main app with the new spinner ID
          navigate('/', { state: { spinnerId: newSpinnerId } });
          
          if (auth.currentUser) {
            toast.success('Spinner copied to your account!');
          } else {
            toast.success('Spinner copied to your local storage!');
          }
        }
      } catch (error) {
        console.error('Error creating spinner copy:', error);
        toast.error(t('share.error'));
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    validateAndCreateCopy();
  }, [spinnerId, navigate, t]);

  // Generate dynamic meta tags based on spinner data
  const generateMetaTags = () => {
    if (!spinnerData) {
      return {
        title: 'Spinner Games !! An Interactive Spinner with fun Games',
        description: '🎯 Create custom spinners, play fun games like Would You Rather, Truth or Dare, and make decisions with our interactive spinner app. Perfect for parties and daily choices!',
        image: 'https://spinnergames.fun/social-share.png',
        siteName: 'Spinner Games !!'
      };
    }

    const spinnerName = spinnerData.name || 'Untitled Spinner';
    const participantCount = spinnerData.participants?.length || 0;
    const gameType = spinnerData.style || 'Custom Spinner';
    
    return {
      title: `${spinnerName} - Spinner Games !!`,
      description: `🎯 Check out this ${gameType} with ${participantCount} participants! Spin the wheel and have fun with friends. Create your own spinner at Spinner Games !!`,
      image: 'https://spinnergames.fun/social-share.png',
      siteName: 'Spinner Games !!'
    };
  };

  const metaTags = generateMetaTags();
  const currentUrl = `${window.location.origin}/share/${spinnerId}`;

  // Show loading screen while validating spinner and creating copy
  if (loading) {
    return (
      <>
        <Helmet>
          <title>{metaTags.title}</title>
          <meta name="description" content={metaTags.description} />
          
          {/* Open Graph Meta Tags */}
          <meta property="og:title" content={metaTags.title} />
          <meta property="og:description" content={metaTags.description} />
          <meta property="og:url" content={currentUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content={metaTags.siteName} />
          <meta property="og:locale" content="en_US" />
          <meta property="og:image" content={metaTags.image} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:type" content="image/png" />
          <meta property="og:image:alt" content="Spinner Games !! Logo" />
          <meta property="og:image:secure_url" content={metaTags.image} />
          
          {/* Twitter Card Meta Tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={metaTags.title} />
          <meta name="twitter:description" content={metaTags.description} />
          <meta name="twitter:image" content={metaTags.image} />
          <meta name="twitter:url" content={currentUrl} />
          <meta name="twitter:site" content="@spinnergames" />
          
          {/* Additional Meta Tags for Better Compatibility */}
          <meta name="robots" content="index, follow" />
          <meta name="author" content="Spinner Games !!" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <link rel="canonical" href={currentUrl} />
        </Helmet>
        <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white mx-auto"></div>
            <p className="mt-4 text-gray-700 dark:text-gray-300">Creating your copy...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{metaTags.title}</title>
        <meta name="description" content={metaTags.description} />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content={metaTags.title} />
        <meta property="og:description" content={metaTags.description} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={metaTags.siteName} />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content={metaTags.image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:alt" content="Spinner Games !! Logo" />
        <meta property="og:image:secure_url" content={metaTags.image} />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTags.title} />
        <meta name="twitter:description" content={metaTags.description} />
        <meta name="twitter:image" content={metaTags.image} />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:site" content="@spinnergames" />
        
        {/* Additional Meta Tags for Better Compatibility */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Spinner Games !!" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href={currentUrl} />
      </Helmet>
      <Frame />
    </>
  );
}; 